// src/contexts/FileContext.js - Fixed version
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const FileContext = createContext();

export const FileProvider = ({ children }) => {
  const [isLoading, setLoading] = useState(false);
const [currentFolder, setCurrentFolderState] = useState({ items: [] });
  const [currentPath, setCurrentPath] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortType, setSortType] = useState('all');
  const [sortMethod, setSortMethod] = useState('alphabetical');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Upload state
  const [isUploadInProgress, setUploadInProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [failedFiles, setFailedFiles] = useState([]);
  
  // Reference to the abort controller
  const controllerRef = useRef(null);

  const setCurrentFolder = useCallback((newFolder) => {

  setCurrentFolderState(newFolder);
}, [currentFolder]);
  
  // Create a new controller when needed
  const createController = useCallback(() => {
    if (!controllerRef.current) {
      controllerRef.current = new AbortController();
    }
    return controllerRef.current;
  }, []);
  
  // Reset the controller
  const resetController = useCallback(() => {
    controllerRef.current = new AbortController();
    return controllerRef.current;
  }, []);
  
  // Abort the current operation and create a new controller
  const abortAndReset = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = new AbortController();
    }
  }, []);
  
  // Update upload progress
  const updateUploadProgress = useCallback((progress, message = '') => {
    setUploadProgress(progress);
    if (message) setUploadMessage(message);
    if (progress > 0 && progress < 100) {
      setUploadInProgress(true);
    }
    if (progress >= 100) {
      setTimeout(() => {
        setUploadInProgress(false);
      }, 3000);
    }
  }, []);
  
  // Add a failed file to the list
  const addFailedFile = useCallback((file) => {
    setFailedFiles(prev => [...prev, file]);
  }, []);
  
  // Reset upload state
  const resetUploadState = useCallback(() => {
    setUploadInProgress(false);
    setUploadProgress(0);
    setUploadMessage('');
    setFailedFiles([]);
  }, []);
  
  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  // Toggle item selection
const toggleItemSelection = (item) => {
  setSelectedItems(prevSelected => {
    // Use array index as primary identifier for duplicates
    const itemIdentifier = item.arrayIndex !== undefined 
      ? `${item.arrayIndex}-${item.name}-${item.type}`
      : (item.path || item.name);
    
    const existingIndex = prevSelected.findIndex(selectedItem => {
      const selectedIdentifier = selectedItem.arrayIndex !== undefined
        ? `${selectedItem.arrayIndex}-${selectedItem.name}-${selectedItem.type}`
        : (selectedItem.path || selectedItem.name);
      
      return selectedIdentifier === itemIdentifier;
    });

    if (existingIndex >= 0) {
      // Item is already selected, remove it
      return prevSelected.filter((_, index) => index !== existingIndex);
    } else {
      // Item is not selected, add it
      return [...prevSelected, item];
    }
  });
};

  const value = {
    isLoading,
    setLoading,
    currentFolder,
    setCurrentFolder,
    currentPath,
    setCurrentPath,
    viewMode,
    setViewMode,
    selectedItems,
    setSelectedItems,
    clearSelection,
    searchQuery,
    setSearchQuery,
    sortType,
    setSortType,
    sortMethod,
    setSortMethod,
    sortOrder,
    setSortOrder,
    isUploadInProgress,
    setIsUploadInProgress: setUploadInProgress,
    uploadProgress,
    uploadMessage,
    failedFiles,
    setUploadProgress: updateUploadProgress,
    addFailedFile,
    resetUploadState,
    controller: controllerRef.current,
    createController,
    resetController,
    abortAndReset,
    toggleItemSelection
  };

  return (
    <FileContext.Provider value={value}>
      {children}
    </FileContext.Provider>
  );
};

export const useFileContext = () => useContext(FileContext);

export default FileContext;